import { memo } from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  Edit,
  Ban,
  PackageCheck,
  PackageOpen,
  ChevronLeft,
  ChevronRight,
  Check,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { formatCurrency, formatDateTime } from "@/shared/lib/formatters";
import { RentalStatusBadge } from "./RentalStatusBadge";
import { RENTAL_TYPE_LABELS } from "./RentalTypeBadge";
import type { RentalResponseDTO } from "../types/rental.types";
import { RentalStatus } from "@/shared/types/enums";

const TERMINAL_STATUSES: RentalStatus[] = [
  RentalStatus.CANCELED,
  RentalStatus.FINISHED,
];

interface DeepLinkButtonProps {
  enabled: boolean;
  to: string;
  title: string;
  ariaLabel: string;
  icon: typeof Eye;
}

/**
 * Renderiza um `<Button disabled>` quando bloqueado (visual disabled real
 * via primitivo do shadcn) ou um `<Button asChild><Link></Button>` quando
 * ativo. Evita a armadilha de aplicar `disabled` em `<a>`, que não
 * propaga estilos.
 */
function DeepLinkButton({
  enabled,
  to,
  title,
  ariaLabel,
  icon: Icon,
}: DeepLinkButtonProps) {
  if (!enabled) {
    return (
      <Button
        variant="ghost"
        size="icon-sm"
        disabled
        title={title}
        aria-label={ariaLabel}
      >
        <Icon className="w-4 h-4" />
      </Button>
    );
  }
  return (
    <Button asChild variant="ghost" size="icon-sm" title={title}>
      <Link to={to} aria-label={ariaLabel}>
        <Icon className="w-4 h-4" />
      </Link>
    </Button>
  );
}

interface RentalsTableProps {
  rentals: RentalResponseDTO[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onView: (rental: RentalResponseDTO) => void;
  onEdit: (rental: RentalResponseDTO) => void;
  onCancel: (rental: RentalResponseDTO) => void;
  onPageChange: (page: number) => void;
}

/**
 * Tabela paginada de aluguéis (USER) com badge de status, ícone de
 * sapatos e botões de ação por linha. Os deeplinks (Retirar, Devolver,
 * Completar) levam a rotas aninhadas em `/alugueis/:id/...` — telas
 * podem não existir ainda; nesse caso o link cai em 404 até elas
 * serem criadas.
 *
 * @example
 * <RentalsTable
 *   rentals={list.data}
 *   loading={list.loading}
 *   currentPage={list.page}
 *   totalPages={list.totalPages}
 *   onView={openDetails}
 *   onEdit={openEdit}
 *   onCancel={openCancel}
 *   onPageChange={list.setPage}
 * />
 */
export const RentalsTable = memo(function RentalsTable({
  rentals,
  loading,
  currentPage,
  totalPages,
  onView,
  onEdit,
  onCancel,
  onPageChange,
}: RentalsTableProps) {
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Contrato</TableHead>
              <TableHead>Vendedor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Saída prevista</TableHead>
              <TableHead>Retorno previsto</TableHead>
              <TableHead className="text-center">Sapatos</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rentals.map((rental) => {
              const isTerminal = TERMINAL_STATUSES.includes(rental.status);
              const canPickUp = rental.status === "COMPLETE";
              const canReturn = rental.status === "ON_STREET";

              const pickupTo = `/retirada?rentalId=${rental.id}`;
              const returnTo = `/devolucoes?rentalId=${rental.id}`;
              return (
                <TableRow key={rental.id}>
                  <TableCell>{RENTAL_TYPE_LABELS[rental.rentalType]}</TableCell>
                  <TableCell>{rental.clientName}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {rental.contractNumber}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {rental.sellerName}
                  </TableCell>
                  <TableCell>
                    <RentalStatusBadge status={rental.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(rental.totalValue)}
                  </TableCell>
                  <TableCell>{formatDateTime(rental.expectedOutAt)}</TableCell>
                  <TableCell>{formatDateTime(rental.expectedInAt)}</TableCell>
                  <TableCell className="text-center text-muted-foreground">
                    {rental.hasShoes ? (
                      <Check
                        className="w-4 h-4 inline"
                        aria-label="Com sapatos"
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onView(rental)}
                        title="Ver detalhes"
                        aria-label="Ver detalhes do aluguel"
                        disabled={loading}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Can permission={ACTIONS.rentals.pickup}>
                        <DeepLinkButton
                          enabled={canPickUp && !loading}
                          to={pickupTo}
                          title={
                            canPickUp
                              ? "Retirar aluguel"
                              : "Disponível apenas para completos"
                          }
                          ariaLabel="Retirar aluguel"
                          icon={PackageOpen}
                        />
                      </Can>
                      <Can permission={ACTIONS.devolutions.read}>
                        <DeepLinkButton
                          enabled={canReturn && !loading}
                          to={returnTo}
                          title={
                            canReturn
                              ? "Devolver aluguel"
                              : "Disponível apenas em rua"
                          }
                          ariaLabel="Devolver aluguel"
                          icon={PackageCheck}
                        />
                      </Can>
                      <Can permission={ACTIONS.rentals.update}>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onEdit(rental)}
                          title={
                            isTerminal
                              ? "Aluguéis cancelados ou finalizados não podem ser editados"
                              : "Editar"
                          }
                          aria-label="Editar aluguel"
                          disabled={isTerminal || loading}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </Can>
                      <Can permission={ACTIONS.rentals.cancel}>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onCancel(rental)}
                          title={
                            isTerminal
                              ? "Aluguéis cancelados ou finalizados não podem ser cancelados"
                              : "Cancelar"
                          }
                          aria-label="Cancelar aluguel"
                          disabled={isTerminal || loading}
                        >
                          <Ban className="w-4 h-4 text-destructive" />
                        </Button>
                      </Can>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                const showPage =
                  i === 0 ||
                  i === totalPages - 1 ||
                  (i >= currentPage - 1 && i <= currentPage + 1);

                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
